import { config } from 'config'
import client from 'graphql/client'
import { GET_USER_ROLES } from 'graphql/queries'
import localforage from 'localforage'
import { getMessageToken, removeMessageToken } from './Firebase'

async function postData(payload: { [key: string]: string | undefined }) {
  const formData = new FormData()

  for (const [key, value] of Object.entries(payload)) {
    if (value) {
      formData.append(key, value)
    }
  }

  return await fetch(config.API_URL + '/oauth/token', {
    method: 'POST',
    body: formData,
  })
}

class AuthApi {
  _tokenKey = 'skvr-zp-auth'
  _permanentLoginKey = 'skvr-zp-auth-permanent'
  _userIdKey = 'skvr-zp-user-id'

  store = localforage.createInstance({ name: this._userIdKey })

  // A refresh in progress, shared by every caller that wants a token while it
  // runs. The server issues a new refresh token on each use and revokes the old
  // one, so two refreshes with the same token are not both safe: whichever
  // arrives after the first has been consumed comes back 401 and used to end
  // the session. Access tokens last 300s, so several requests hitting that
  // boundary together is routine rather than exceptional.
  _refreshInFlight: Promise<{ access_token: string } | null> | null = null

  async login({ username, password, permanentLogin }: { username: string; password: string; permanentLogin: boolean }) {
    let result = await postData({
      username: username,
      password: password,
      client_secret: config.OAUTH_CLIENT_KEY,
      grant_type: 'password',
      client_id: config.OAUTH_CLIENT_ID,
      scope: config.OAUTH_SCOPE,
    })
    const resultJson = await result.json()
    if (result?.status === 200) {
      window.localStorage.setItem(this._permanentLoginKey, JSON.stringify(permanentLogin))
      this.updateToken(resultJson)
      const { isProfessional, id } = await this.checkPermissions()
      if (!isProfessional) {
        await this.logout()
        return { error: { message: 'This user is not allowed' } }
      }
      this.storage().setItem(this._userIdKey, id)
      this.store.setItem(this._userIdKey, id)
      getMessageToken(id)
    }
    return resultJson
  }

  storage() {
    let result = window.localStorage.getItem(this._permanentLoginKey)
    if (!result) {
      return window.sessionStorage
    }
    const permanentLogin = JSON.parse(result)
    if (permanentLogin) {
      return window.localStorage
    }
    return window.sessionStorage
  }

  updateToken(token: { access_token: string; refresh_token: string; expires_in: number } | null) {
    if (token) {
      // adding amount of seconds token will expired in (in ms) to current time
      const expiration_date = new Date().getTime() + token.expires_in * 1000
      this.storage().setItem(
        this._tokenKey,
        JSON.stringify({ access: token.access_token, refresh: token.refresh_token, expiration_date: expiration_date }),
      )
      this.store.setItem(this._tokenKey, token.access_token)
    } else {
      this.storage().removeItem(this._tokenKey)
      this.store.removeItem(this._tokenKey)
    }
  }

  async getAccessToken() {
    let result = this.storage().getItem(this._tokenKey)
    if (!result) {
      return ''
    }
    const authData = JSON.parse(result)

    // Refresh a few seconds early. Tokens live 300s, and a token sent at 299s
    // can be rejected by the time it arrives — which then reads as a dead
    // session rather than an expired token.
    const REFRESH_MARGIN_MS = 15000
    const isExpired = new Date().getTime() > authData.expiration_date - REFRESH_MARGIN_MS

    if (isExpired) {
      const newToken = await this.refresh()

      // A failed refresh that did not sign us out (a network blip) leaves the
      // existing token in place; sending it is better than sending nothing.
      return newToken?.access_token ?? authData.access ?? ''
    }

    return authData.access
  }

  getRefreshToken() {
    let result = this.storage().getItem(this._tokenKey)
    if (!result) {
      return ''
    }
    return JSON.parse(result).refresh
  }

  tokenIsExist() {
    return !!this.storage().getItem(this._tokenKey)
  }

  async logout() {
    await removeMessageToken(this.getUserId())
    this.updateToken(null)
    this.storage().removeItem(this._userIdKey)
    this.store.removeItem(this._userIdKey)
  }

  getUserId() {
    return Number(this.storage().getItem(this._userIdKey))
  }

  async refresh() {
    // Collapse concurrent refreshes into one request. Without this, each caller
    // sent its own with the same stored refresh token, and any that lost the
    // race got a 401 for a session that was perfectly valid.
    if (!this._refreshInFlight) {
      this._refreshInFlight = this._performRefresh().finally(() => {
        this._refreshInFlight = null
      })
    }

    return this._refreshInFlight
  }

  async _performRefresh(): Promise<{ access_token: string } | null> {
    const refreshToken = this.getRefreshToken()

    if (!refreshToken) {
      // Nothing to refresh with, so the session really is over.
      await this.logout()
      return null
    }

    try {
      const result = await postData({
        grant_type: 'refresh_token',
        client_secret: config.OAUTH_CLIENT_KEY,
        client_id: config.OAUTH_CLIENT_ID,
        scope: config.OAUTH_SCOPE,
        refresh_token: refreshToken,
      })

      if (result?.status === 200) {
        const resultJson = await result.json()
        this.updateToken(resultJson)
        return resultJson
      }

      // A refusal here does not always mean the session is over. The server
      // consumes a refresh token on use and issues a new one, so if a second
      // tab — or the PDF tab this app opens — refreshed a moment ago, the token
      // just sent was already spent and comes back 401 even though the session
      // is alive and well. Single-flight only dedupes within one tab, so this
      // is exactly what signs people out at random while they work.
      //
      // Storage is the shared state, so ask it: if it now holds a different
      // refresh token, somebody else won the race and left a usable session
      // behind. Adopt it rather than throwing everyone out.
      if (result?.status === 400 || result?.status === 401) {
        const stored = this.getRefreshToken()

        if (stored && stored !== refreshToken) {
          const current = this.storage().getItem(this._tokenKey)

          if (current) {
            return { access_token: JSON.parse(current).access }
          }
        }
      }

      // Only a refusal of the credentials ends the session. A 500 or a gateway
      // error says nothing about whether the refresh token is still good, and
      // signing out over one is how a brief server hiccup used to log people
      // out mid-task.
      if (result?.status === 400 || result?.status === 401) {
        await this.logout()
      }

      return null
    } catch {
      // Network failure: the token may well still be valid, so keep the session
      // and let the next request try again.
      return null
    }
  }

  async checkPermissions() {
    try {
      const { data } = await client.query({ query: GET_USER_ROLES, fetchPolicy: 'network-only' })
      return {
        isProfessional: data.current_professional.roles.includes(config.OAUTH_SCOPE),
        id: data.current_professional.id,
      }
    } catch {
      return {
        isProfessional: false,
        id: '',
      }
    }
  }
}

export const authApi = new AuthApi()
export const userIdKey = authApi._userIdKey
export const accessTokenKey = authApi._tokenKey
